import { useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import { cn } from "@/shared/lib/utils";
import { AppModal } from "@/shared/components/dialog";
import { Button } from "@/shared/components/button";
import { Switch } from "@/shared/components/misc";
import type { ConsentBadge, ConsentItem } from "@/shared/types/consent";

const CONSENT_PRIVACY_PATH = "/patient/consent-privacy";

interface ConsentPrivacySectionProps {
  items: ConsentItem[];
}

function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const datePart = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(date);
  const timePart = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).format(date);
  return `${datePart}, ${timePart}`;
}

const CARD_ICONS: Record<string, string> = {
  "platform-terms": "lucide:shield",
  "data-processing": "lucide:settings",
  "ai-access": "lucide:brain",
  "data-export": "lucide:download",
};

const BADGE_TEXT: Record<ConsentBadge, string> = {
  required: "Required",
  core: "Core Functionality",
  optional: "Optional",
};

function ConsentCard({ item, onToggle }: { item: ConsentItem; onToggle: () => void }) {
  const iconName = CARD_ICONS[item.id] ?? "lucide:shield";
  const isOn = item.locked || item.active;

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl border border-l-4 border-[#E5E7EB] bg-white p-5 shadow-sm",
        isOn ? "border-l-[#F36F8F]" : "border-l-[#111827]",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-full", isOn ? "bg-[#F36F8F]" : "bg-[#6B7280]")}>
            <Icon icon={iconName} className="h-5 w-5 text-white" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-extrabold text-[#0A2947]">{item.title}</h3>
            <span
              className={cn(
                "rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide",
                isOn ? "bg-[#F36F8F]/15 text-[#E4002B]" : "bg-[#F3F4F6] text-[#6B7280]",
              )}
            >
              {BADGE_TEXT[item.badge]}
            </span>
          </div>
        </div>

        {item.locked ? (
          <Button
            variant="outline"
            disabled
            className="!flex !h-auto !shrink-0 !items-center !gap-1.5 !rounded-full !border-[#E5E7EB] !px-3 !py-1.5 !text-xs !font-semibold !text-[#6B7280]"
          >
            <Icon icon="lucide:lock" className="h-3.5 w-3.5" />
            Locked
          </Button>
        ) : (
          <div className="flex shrink-0 items-center gap-2">
            <span className={cn("text-xs font-semibold", item.active ? "text-[#E4002B]" : "text-[#9CA3AF]")}>
              {item.active ? "Active" : "Inactive"}
            </span>
            <Switch checked={item.active} onCheckedChange={onToggle} />
          </div>
        )}
      </div>

      <p className="mt-3 text-sm text-[#4B5563]">{item.description}</p>

      {item.warningText && item.active && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-sm text-[#92400E]">
          <Icon icon="lucide:alert-triangle" className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{item.warningText}</p>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-[#E5E7EB] pt-3 text-xs text-[#9CA3AF]">
        <span className="flex items-center gap-1.5">
          <Icon icon="lucide:clock" className="h-3.5 w-3.5" />
          Last updated <span className="font-semibold text-[#111827]">{formatDateTime(item.lastUpdated)}</span>
        </span>
        {item.links && (
          <div className="flex flex-wrap items-center gap-4">
            {item.links.map((link) => (
              <Link
                key={link.label}
                to={link.url}
                state={{ from: CONSENT_PRIVACY_PATH }}
                className="flex items-center gap-1 font-semibold text-[#E4002B] hover:underline"
              >
                {link.label}
                <Icon icon="lucide:external-link" className="h-3 w-3" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CoverageWidget({ items, activeCount, total, percent }: { items: ConsentItem[]; activeCount: number; total: number; percent: number }) {
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - percent / 100);

  const legend: { key: string; label: string; icon: string; color: string }[] = [
    { key: "platform-terms", label: "Legal", icon: "lucide:scroll-text", color: "#16A34A" },
    { key: "data-processing", label: "Data", icon: "lucide:flask-conical", color: "#2563EB" },
    { key: "ai-access", label: "AI", icon: "lucide:brain", color: "#9333EA" },
    { key: "data-export", label: "Export", icon: "lucide:download", color: "#EA580C" },
  ];

  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-extrabold text-[#0A2947]">Consent Coverage</h3>
        <span className="text-xs text-[#9CA3AF]">
          {activeCount} of {total} active
        </span>
      </div>

      <div className="mt-4 flex items-center gap-4">
        <div className="relative flex h-20 w-20 shrink-0 items-center justify-center">
          <svg viewBox="0 0 100 100" className="h-20 w-20 -rotate-90">
            <circle cx="50" cy="50" r={radius} fill="none" stroke="#E5E7EB" strokeWidth="10" />
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke="#0A2947"
              strokeWidth="10"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
            />
          </svg>
          <span className="absolute text-lg font-extrabold text-[#111827]">{percent}%</span>
        </div>
        <div>
          <p className="text-sm font-extrabold text-[#111827]">{percent === 100 ? "All set" : "Needs attention"}</p>
          <p className="mt-0.5 text-xs text-[#6B7280]">
            {percent === 100 ? "Every consent is active." : "Review inactive consents to ensure your record is complete."}
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-2.5 border-t border-[#E5E7EB] pt-4">
        {legend.map((entry) => {
          const item = items.find((i) => i.id === entry.key);
          const isActive = item ? item.locked || item.active : false;
          return (
            <div key={entry.key} className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-[#4B5563]">
                <Icon icon={entry.icon} className="h-4 w-4" style={{ color: entry.color }} />
                {entry.label}
              </div>
              <span className={cn("h-2 w-2 rounded-full", isActive ? "bg-[#F36F8F]" : "bg-[#D1D5DB]")} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TurnOffModal({ item, onCancel, onConfirm }: { item: ConsentItem; onCancel: () => void; onConfirm: () => void }) {
  return (
    <AppModal
      open
      onOpenChange={(open) => { if (!open) onCancel(); }}
      width={384}
      className="[&_.ant-modal-content]:!rounded-3xl"
    >
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#F36F8F]">
          <Icon icon="lucide:alert-triangle" className="h-5 w-5 text-white" />
        </div>
        <div>
          <h2 className="text-base font-extrabold text-[#180047]">Turn this off?</h2>
          <p className="mt-1 text-sm text-[#6B7280]">{item.turnOffWarning}</p>
        </div>
      </div>
      <div className="mt-6 flex items-center gap-3">
        <Button variant="outline" onClick={onCancel} className="!h-auto !rounded-full !border-[#F36F8F] !px-5 !py-2.5 !text-sm !font-semibold !text-[#E4002B] hover:!border-[#F36F8F] hover:!bg-[#FFF2F4] hover:!text-[#E4002B]">
          Cancel
        </Button>
        <Button onClick={onConfirm} className="!h-auto !rounded-full !bg-[#F36F8F] !px-5 !py-2.5 !text-sm !font-semibold !text-white hover:!bg-[#F36F8F] hover:!opacity-90">
          I understand, turn off
        </Button>
      </div>
    </AppModal>
  );
}

export function ConsentPrivacySection({ items: initialItems }: ConsentPrivacySectionProps) {
  const [items, setItems] = useState(initialItems);
  const [confirmTargetId, setConfirmTargetId] = useState<string | null>(null);

  const activeCount = items.filter((i) => i.active).length;
  const total = items.length;
  const percent = total === 0 ? 0 : Math.round((activeCount / total) * 100);
  const confirmTarget = items.find((i) => i.id === confirmTargetId) ?? null;

  const setActive = (id: string, active: boolean) => {
    setItems((current) => current.map((i) => (i.id === id ? { ...i, active, lastUpdated: new Date().toISOString() } : i)));
  };

  const handleToggle = (item: ConsentItem) => {
    if (item.locked) return;
    if (item.active) {
      setConfirmTargetId(item.id);
    } else {
      setActive(item.id, true);
    }
  };

  const confirmTurnOff = () => {
    if (!confirmTarget) return;
    setActive(confirmTarget.id, false);
    setConfirmTargetId(null);
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="grid items-start gap-5 lg:grid-cols-[1fr_320px]">
        <div className="space-y-5">
          {items.map((item) => (
            <ConsentCard key={item.id} item={item} onToggle={() => handleToggle(item)} />
          ))}
        </div>

        {/* Sticky so it stays in view while scrolling a long consent-cards
            list, instead of scrolling away above the fold (matches the
            pattern in FhirExportView's "Export Summary"). */}
        <div className="sticky top-5 space-y-4">
          <CoverageWidget items={items} activeCount={activeCount} total={total} percent={percent} />
          <div className="rounded-2xl border border-[#DBEAFE] bg-[#EFF6FF] p-4 text-xs leading-relaxed text-[#4B5563]">
            All changes are timestamped and logged for your protection. Questions?{" "}
            <a href="mailto:privacy@medsynciq.com" className="font-semibold text-[#E4002B] hover:underline">
              privacy@medsynciq.com
            </a>
          </div>
        </div>
      </div>

      {confirmTarget && (
        <TurnOffModal item={confirmTarget} onCancel={() => setConfirmTargetId(null)} onConfirm={confirmTurnOff} />
      )}
    </div>
  );
}
